'use client'

import {
    PaymentQuoteOption,
    QuoteMethod,
    getPaymentOptions,
} from '@/lib/api/financial/financial-billing-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdCreditCard,
    MdPix,
    MdRemove,
    MdSchedule,
    MdTrendingUp,
    MdTune,
} from 'react-icons/md'

/**
 * Chave da opção "decidir na tela de pagamento". Não é método: é a ausência
 * deliberada de um, e por isso não existe no backend.
 */
const CUSTOM_KEY = '__CUSTOM__'

/**
 * Chave do pagamento FLEX. Também não é método: é um regime de pagamento do
 * procedimento — quem escolhe forma e valor, ali, é o paciente, a cada parcela
 * avulsa que ele decide pagar pelo portal.
 */
const FLEX_KEY = '__FLEX__'

/**
 * Chave do "gerar cobrança depois". Também não é método: é a decisão de NÃO emitir
 * cobrança nenhuma agora — o paciente paga no dia do atendimento, na recepção.
 */
const DEFER_KEY = '__DEFER__'

/**
 * Quanto o paciente paga em cada forma de pagamento — o número que o operador fala.
 *
 * O valor do serviço não é o que o paciente paga: a taxa da forma escolhida entra por
 * cima. Antes disto, o operador fechava "R$ 300,00 no Pix" e o link chegava com outro
 * valor, sem que ninguém conseguisse explicar a diferença.
 *
 * A conta vem do backend (`GET financial/billings/payment-options`), que usa o mesmo
 * cálculo da criação da cobrança e a mesma política de parcelamento do checkout. Não
 * replicar a regra aqui é o ponto: cópia de regra financeira no front já divergiu antes
 * e fez a tela mentir.
 *
 * A palavra "taxa" não aparece — o valor é sempre apresentado embutido.
 */

const METHOD_LABEL: Record<QuoteMethod, string> = {
    PIX: 'Pix',
    CREDIT_CARD: 'Cartão de crédito',
    DEBIT_CARD: 'Cartão de débito',
    BOLETO: 'Boleto',
}

const formatCents = (cents: number): string =>
    `R$ ${(cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`

interface PaymentOptionsProps {
    /** Valor do serviço em centavos, SEM taxa. */
    amount: number
    /**
     * De onde o componente foi montado (`scheduling`, `budget`, ...).
     *
     * Hoje NÃO muda o cálculo — está aqui porque as telas já identificam a origem e,
     * quando surgir a primeira regra por origem, ela entra sem tocar em quem chama.
     * Enquanto isso, não é enviado ao backend: parâmetro que o servidor ignora vira
     * mentira na primeira leitura do código.
     */
    context?: string
    /** Restringe as formas exibidas. Sem isto, todas as habilitadas no checkout. */
    methods?: QuoteMethod[]
    /** Avisa a tela quando o operador escolhe uma forma (para gravar a preferência). */
    onSelect?: (option: PaymentQuoteOption) => void
    /**
     * Escolha JÁ FEITA, para reabrir a tela no que foi combinado.
     *
     * Sem isto, editar um atendimento mostraria o componente zerado e o operador teria
     * de escolher de novo — ou pior, salvaria sem escolher e a cobrança perderia o
     * parcelamento acordado.
     */
    selectedMethod?: string | null
    selectedInstallments?: number | null
    /**
     * Escolher "na tela de pagamento" — o fluxo antigo, que segue existindo.
     *
     * Este componente só oferece o que passa pelo gateway (Pix, débito, crédito).
     * **Dinheiro, maquininha, "outro" e o split entre várias formas existem apenas no
     * formulário da cobrança** — sem esta saída, fechar a venda em dinheiro passou a
     * ser impossível pelo agendamento.
     *
     * Quem recebe deve LIMPAR o método escolhido: a cobrança volta a nascer sem
     * forma definida e o operador é levado à tela de pagamento, como antes.
     * Ausente, a opção não aparece (é o caso do próprio formulário da cobrança, onde
     * o operador já está).
     */
    onCustomize?: () => void
    /**
     * A opção correspondente à escolha ATUAL, sempre que ela muda — inclusive quando a
     * escolha veio de fora (edição) ou quando o valor mudou e o total foi recalculado.
     *
     * Existe porque quem mostra o "Valor Final" na tela não é este componente: sem
     * isto, o resumo continuava exibindo o valor do serviço enquanto a linha escolhida
     * aqui já mostrava outro número — e o operador precisava confiar no menor dos dois.
     * `onSelect` não serve para isso: só dispara no clique.
     */
    onQuoteChange?: (option: PaymentQuoteOption | null) => void
    /**
     * Ligar o PAGAMENTO FLEX — só o procedimento tem.
     *
     * Ausente, a opção não aparece; é o caso de exame, consulta, orçamento e de um
     * procedimento que já está em flex. Quem oferece decide se cabe (ver
     * `enableFlexPayment` no backend, que recusa orçamento e cancelado).
     *
     * Fica ao lado das formas, e não escondido num menu da tela de detalhes, porque
     * é aqui que a conversa sobre como o paciente vai pagar acontece — quem acabou
     * de ouvir "não consigo fechar tudo agora" está nesta tela, não em outra.
     *
     * Como o "decidir depois", limpa a forma escolhida: no flex ninguém acerta
     * método nem parcela na venda.
     */
    onFlex?: () => void
    /** O flex já foi escolhido nesta edição (a tela é dona do estado). */
    flexSelected?: boolean
    /**
     * COBRAR DEPOIS — agenda sem emitir cobrança nenhuma.
     *
     * A maioria dos pacientes marca hoje e paga no dia, na recepção. Sem esta saída, todo
     * agendamento nascia com uma cobrança que ninguém esperava receber ainda: o financeiro
     * enchia de pendências e o lembrete de vencimento saía para quem ia pagar presencialmente.
     *
     * Não é "decidir depois" (`onCustomize`), que EMITE a cobrança e só deixa a forma em
     * aberto; aqui não nasce cobrança alguma. O valor do atendimento fica gravado, e a
     * cobrança sai quando quiserem, pelo "Gerar cobrança" da tela de detalhe.
     *
     * Ausente, a opção não aparece — é o caso do formulário da cobrança, onde ela já existe.
     */
    onDefer?: () => void
    /** O "cobrar depois" já foi escolhido nesta edição (a tela é dona do estado). */
    deferSelected?: boolean
    /**
     * Esconde as formas do gateway, mantendo só as saídas (flex, "decidir depois").
     *
     * Serve à edição de um atendimento cuja cobrança já foi emitida: ali a forma não
     * muda por este formulário, e listar Pix e cartões seria pedir uma decisão que o
     * salvamento descarta. Também evita a consulta de cotação, que não teria uso.
     */
    hideMethods?: boolean
    /**
     * De onde vem a cotação. Sem isto, a rota das cobranças
     * (`financial/billings/payment-options`), que é o caso de toda tela de
     * atendimento.
     *
     * Existe porque a MESMA cotação é servida por rotas com permissões diferentes:
     * o simulador de venda é do vendedor, que não tem acesso a cobranças, e usar a
     * rota do billing faria a tela abrir com a lista vazia e um 403 que ninguém vê.
     */
    fetchOptions?: (
        amount: number,
        methods?: QuoteMethod[]
    ) => Promise<PaymentQuoteOption[]>
    className?: string
}

export default function PaymentOptions({
    amount,
    methods,
    onSelect,
    selectedMethod = null,
    selectedInstallments = null,
    onCustomize,
    onQuoteChange,
    onFlex,
    flexSelected = false,
    onDefer,
    deferSelected = false,
    hideMethods = false,
    fetchOptions,
    className = '',
}: PaymentOptionsProps) {
    const [options, setOptions] = useState<PaymentQuoteOption[]>([])
    const [loading, setLoading] = useState(false)
    /** Incrementado para forçar um recálculo sem que o valor tenha mudado. */
    const [refreshKey, setRefreshKey] = useState(0)
    const [selected, setSelected] = useState<string | null>(null)
    /**
     * Posição na lista de parcelamentos permitidos — índice, não número de parcelas.
     *
     * O que é permitido não é uma sequência: os tiers "Nx a partir de R$ Y" liberam
     * parcelas conforme o valor, então 2x pode existir e 3x não. Andar pelo índice
     * percorre exatamente o que o backend liberou; contar de um em um ofereceria
     * parcelamento que a cobrança recusaria depois.
     */
    const [installmentIndex, setInstallmentIndex] = useState(0)

    // O valor muda a cada tecla no campo de preço; sem debounce seria uma chamada
    // por caractere digitado.
    const debouncedAmount = useDebounce(amount, 400)
    const methodsKey = methods?.join(',') || ''

    useEffect(() => {
        // Sem lista de formas não há cotação a pedir: o componente está ali só pelas
        // saídas (flex, "decidir depois").
        if (hideMethods || !(debouncedAmount > 0)) {
            setOptions([])
            return
        }
        let cancelled = false
        setLoading(true)
        const load = fetchOptions
            ? fetchOptions(debouncedAmount, methods)
            : getPaymentOptions(debouncedAmount, methods).then(response =>
                  response.success && response.result
                      ? response.result.options
                      : []
              )
        void load
            .then(list => {
                if (cancelled) return
                setOptions(list)
            })
            .catch(() => {
                // Silencioso: o resumo de valor da tela continua válido, só não há
                // detalhamento por forma. Barrar o agendamento por causa disto seria pior.
                if (!cancelled) setOptions([])
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
        // `fetchOptions` fica fora das dependências: quem chama costuma passar uma
        // função nova a cada render, e incluí-la refaria a cotação a cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedAmount, methodsKey, refreshKey, hideMethods])

    /**
     * Refaz o cálculo ao voltar para a aba.
     *
     * A política de taxas é cacheada no backend por empresa e invalidada ao salvar, mas
     * uma tela **já aberta** não sabe disso: quem muda a taxa em outra aba e volta
     * continuaria vendo o número antigo até mexer no valor. O que é cobrado sai certo (o
     * backend recalcula no `create`), então isto é coerência de exibição — mas é o
     * número que o operador acabou de prometer ao paciente.
     *
     * Mesmo padrão de `billings-form-page.tsx`, que refaz os métodos disponíveis no
     * `focus` pela mesma razão.
     */
    useEffect(() => {
        const onFocus = () => setRefreshKey(k => k + 1)
        window.addEventListener('focus', onFocus)
        return () => window.removeEventListener('focus', onFocus)
    }, [])

    /**
     * Começa no MENOR parcelamento — o operador sobe até onde combinou.
     *
     * Já começou no maior, e era perigoso: com o teto em 21x, um clique sem querer na
     * linha fechava a venda em 21 parcelas. O erro no sentido contrário (clicar e
     * fechar em 2x) é visível na hora e barato de desfazer; o outro vira cobrança
     * parcelada em 21 vezes, com a taxa da faixa mais cara.
     *
     * Reabrir uma escolha já feita posiciona no combinado, não no mínimo — senão
     * editar qualquer outra coisa do atendimento rebaixaria o parcelamento acordado.
     *
     * Recalcula quando o valor muda, porque mudar o valor muda o que os tiers liberam:
     * um índice preso apontaria para uma parcela que não existe mais.
     */
    useEffect(() => {
        const parceladas = options.filter(
            o => o.method === 'CREDIT_CARD' && o.installments > 1
        )
        const jaEscolhido =
            selectedMethod === 'CREDIT_CARD' && (selectedInstallments ?? 0) > 1
                ? parceladas.findIndex(
                      o => o.installments === selectedInstallments
                  )
                : -1
        setInstallmentIndex(jaEscolhido >= 0 ? jaEscolhido : 0)
    }, [options, selectedMethod, selectedInstallments])

    /**
     * A escolha vem de fora quando a tela a controla (edição). O estado interno só
     * vale enquanto ninguém informou nada — assim o componente serve tanto para
     * escolher de zero quanto para exibir o que já foi combinado.
     */
    // Flex e "cobrar depois" vencem a forma: escolher um deles limpa o método, e é ele
    // que fica marcado até o operador escolher outra coisa.
    const selectedKey = flexSelected
        ? FLEX_KEY
        : deferSelected
          ? DEFER_KEY
          : selectedMethod
            ? `${selectedMethod}-${selectedInstallments || 1}`
            : selected

    /**
     * Avisa a tela qual é o total da forma escolhida.
     *
     * Guardado em ref porque quem chama costuma passar uma função nova a cada render:
     * como dependência do efeito, isso viraria um laço de atualização.
     */
    const quoteListenerRef = useRef(onQuoteChange)
    quoteListenerRef.current = onQuoteChange

    useEffect(() => {
        if (
            !selectedKey ||
            selectedKey === CUSTOM_KEY ||
            selectedKey === FLEX_KEY ||
            selectedKey === DEFER_KEY
        ) {
            quoteListenerRef.current?.(null)
            return
        }
        quoteListenerRef.current?.(
            options.find(
                o => `${o.method}-${o.installments}` === selectedKey
            ) ?? null
        )
    }, [selectedKey, options])

    if (!(amount > 0)) return null
    // Sem formas do gateway o componente ainda tem razão de existir se houver
    // saída (flex, "decidir depois") — é o caso da edição de cobrança já emitida.
    if (
        !loading &&
        !options.length &&
        !(hideMethods && (onFlex || onCustomize || onDefer))
    )
        return null

    const pix = options.find(o => o.method === 'PIX')
    const debit = options.find(o => o.method === 'DEBIT_CARD')
    const credit = options.filter(o => o.method === 'CREDIT_CARD')
    const creditAtSight = credit.find(o => o.installments === 1)
    const installmentOptions = credit.filter(o => o.installments > 1)
    const currentIndex = Math.min(
        installmentIndex,
        Math.max(0, installmentOptions.length - 1)
    )
    const currentInstallment = installmentOptions[currentIndex] ?? null

    const pick = (option: PaymentQuoteOption) => {
        const key = `${option.method}-${option.installments}`
        setSelected(key)
        onSelect?.(option)
    }

    const Row = ({
        icon,
        label,
        option,
        detail,
    }: {
        icon: React.ReactNode
        label: string
        option: PaymentQuoteOption
        detail?: string
    }) => {
        const key = `${option.method}-${option.installments}`
        const isSelected = selectedKey === key
        return (
            <button
                type="button"
                onClick={() => pick(option)}
                className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition ${
                    isSelected
                        ? 'border-secondary bg-secondary/10'
                        : 'border-base-300 hover:border-base-content/30'
                }`}
            >
                <span className="flex items-center gap-2 text-sm">
                    {icon}
                    {label}
                </span>
                <span className="text-right">
                    <span className="block text-sm font-semibold tabular-nums">
                        {formatCents(option.total)}
                    </span>
                    {detail && (
                        <span className="block text-xs text-base-content/60">
                            {detail}
                        </span>
                    )}
                </span>
            </button>
        )
    }

    return (
        <div
            className={`rounded-xl border border-base-300 bg-base-100 p-3 ${className}`}
        >
            <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-base-content/60">
                    Formas de pagamento
                </span>
                {loading && (
                    <span className="loading loading-spinner loading-xs text-secondary" />
                )}
            </div>

            <div className="flex flex-col gap-2">
                {pix && (
                    <Row
                        icon={<MdPix className="text-base text-secondary" />}
                        label="Pix"
                        option={pix}
                    />
                )}
                {debit && (
                    <Row
                        icon={
                            <MdCreditCard className="text-base text-secondary" />
                        }
                        label={METHOD_LABEL.DEBIT_CARD}
                        option={debit}
                    />
                )}
                {creditAtSight && (
                    <Row
                        icon={
                            <MdCreditCard className="text-base text-secondary" />
                        }
                        label="Cartão à vista"
                        option={creditAtSight}
                    />
                )}
                {/* Parcelado: UMA linha com contador, não uma linha por parcela.
                    Com o teto em 21x a lista tomava a tela inteira e obrigava a
                    procurar; aqui o operador anda até o número que vai falar e o
                    valor acompanha. */}
                {currentInstallment && (
                    <div
                        className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 transition ${
                            selectedKey ===
                            `CREDIT_CARD-${currentInstallment.installments}`
                                ? 'border-secondary bg-secondary/10'
                                : 'border-base-300'
                        }`}
                    >
                        <button
                            type="button"
                            onClick={() => pick(currentInstallment)}
                            className="flex flex-1 items-center gap-2 text-left text-sm"
                        >
                            <MdCreditCard className="text-base text-secondary" />
                            Cartão parcelado
                        </button>

                        <div className="flex items-center gap-3">
                            <div className="join">
                                <button
                                    type="button"
                                    className="btn btn-xs join-item"
                                    disabled={currentIndex <= 0}
                                    aria-label="Menos parcelas"
                                    onClick={() => {
                                        const next = currentIndex - 1
                                        setInstallmentIndex(next)
                                        // Mexer no contador é escolher: sem isto, a
                                        // tela mostraria 6x e a cobrança sairia com o
                                        // que estava selecionado antes.
                                        const option = installmentOptions[next]
                                        if (option) pick(option)
                                    }}
                                >
                                    <MdRemove className="text-sm" />
                                </button>
                                <span className="btn btn-xs join-item pointer-events-none min-w-12 font-semibold tabular-nums">
                                    {currentInstallment.installments}x
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-xs join-item"
                                    disabled={
                                        currentIndex >=
                                        installmentOptions.length - 1
                                    }
                                    aria-label="Mais parcelas"
                                    onClick={() => {
                                        const next = currentIndex + 1
                                        setInstallmentIndex(next)
                                        const option = installmentOptions[next]
                                        if (option) pick(option)
                                    }}
                                >
                                    <MdAdd className="text-sm" />
                                </button>
                            </div>

                            <button
                                type="button"
                                onClick={() => pick(currentInstallment)}
                                className="text-right"
                            >
                                <span className="block text-sm font-semibold tabular-nums">
                                    {formatCents(currentInstallment.total)}
                                </span>
                                <span className="block text-xs text-base-content/60">
                                    {currentInstallment.installments}x de{' '}
                                    {formatCents(
                                        currentInstallment.installmentAmount
                                    )}
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Saída para o fluxo antigo. Fica por último e sem valor ao lado:
                    não é uma forma de pagamento entre as outras, é "decidir depois".
                    Sem ela, dinheiro, maquininha e o split entre formas — que só
                    existem no formulário da cobrança — ficaram inalcançáveis a partir
                    do agendamento. */}
                {onCustomize && (
                    <button
                        type="button"
                        onClick={() => {
                            setSelected(CUSTOM_KEY)
                            onCustomize()
                        }}
                        className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition ${
                            selectedKey === CUSTOM_KEY
                                ? 'border-secondary bg-secondary/10'
                                : 'border-base-300 hover:border-base-content/30'
                        }`}
                    >
                        <MdTune className="mt-0.5 text-base text-base-content/60" />
                        <span>
                            <span className="block text-sm">
                                Outra forma ou dividir o pagamento
                            </span>
                            <span className="block text-xs text-base-content/60">
                                Dinheiro, maquininha ou mais de uma forma na mesma
                                cobrança. Ao salvar, você vai para a tela de pagamento
                                para configurar.
                            </span>
                        </span>
                    </button>
                )}

                {/* PAGAMENTO FLEX (só procedimento). Como o "decidir depois", não
                    tem valor ao lado: no flex não existe "o total desta forma" —
                    quem monta o pagamento é o paciente, uma parcela de cada vez. */}
                {onFlex && (
                    <button
                        type="button"
                        onClick={() => {
                            setSelected(FLEX_KEY)
                            onFlex()
                        }}
                        className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition ${
                            selectedKey === FLEX_KEY
                                ? 'border-secondary bg-secondary/10'
                                : 'border-base-300 hover:border-base-content/30'
                        }`}
                    >
                        <MdTrendingUp className="mt-0.5 text-base text-base-content/60" />
                        <span>
                            <span className="block text-sm">
                                Pagamento flexível
                            </span>
                            <span className="block text-xs text-base-content/60">
                                O paciente paga quanto e quando puder pelo portal,
                                até fechar o total. As cobranças que já existem são
                                mantidas.
                            </span>
                        </span>
                    </button>
                )}

                {/* COBRAR DEPOIS. Fica por último, e sem valor ao lado, porque não é
                    uma forma de pagamento nem uma cobrança em aberto: é a decisão de
                    não emitir nada agora. Diferente do "decidir depois" logo acima, que
                    EMITE a cobrança e só deixa a forma para a tela de pagamento. */}
                {onDefer && (
                    <button
                        type="button"
                        onClick={() => {
                            setSelected(DEFER_KEY)
                            onDefer()
                        }}
                        className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition ${
                            selectedKey === DEFER_KEY
                                ? 'border-secondary bg-secondary/10'
                                : 'border-base-300 hover:border-base-content/30'
                        }`}
                    >
                        <MdSchedule className="mt-0.5 text-base text-base-content/60" />
                        <span>
                            <span className="block text-sm">
                                Gerar cobrança depois
                            </span>
                            <span className="block text-xs text-base-content/60">
                                Nenhuma cobrança é criada agora — o valor fica guardado
                                no atendimento. Emita quando quiser, em "Gerar cobrança"
                                na tela do atendimento.
                            </span>
                        </span>
                    </button>
                )}
            </div>
        </div>
    )
}
